import { useState } from 'react'
import { Box, Container, Typography, Grid, Chip } from '@mui/material'
import { TouchApp, Mouse } from '@mui/icons-material'
import { designSpecs } from '../../theme/designSpecs'

// Import gallery images
import sneakersAfter from '../../assets/home/gallery/sneakers-after.png'
import sneakersBefore from '../../assets/home/gallery/sneakers-before.png'
import skincareAfter from '../../assets/home/gallery/skincare-after.png'
import skincareBefore from '../../assets/home/gallery/skincare-before.png'
import plantAfter from '../../assets/home/gallery/plant-after.png'
import plantBefore from '../../assets/home/gallery/plant-before.png'
import headphonesAfter from '../../assets/home/gallery/headphone-after.png'
import headphonesBefore from '../../assets/home/gallery/headphones-before.png'
import candleAfter from '../../assets/home/gallery/candle-after.png'
import candleBefore from '../../assets/home/gallery/candle-before.png'
import watchAfter from '../../assets/home/gallery/watch-after.png'
import watchBefore from '../../assets/home/gallery/watch-before.png'
import facecreamAfter from '../../assets/home/gallery/facecream-after.png'
import facecreamBefore from '../../assets/home/gallery/facecream-before.png'
import phonecaseAfter from '../../assets/home/gallery/phonecase-after.png'
import phonecaseBefore from '../../assets/home/gallery/phonecase-before.png'
import lipstickAfter from '../../assets/home/gallery/lipstick-after.png'
import lipstickBefore from '../../assets/home/gallery/lipstick-before.png'

const galleryItems = [
  { title: 'Urban Street', before: sneakersBefore, after: sneakersAfter },
  { title: 'Spa Luxury', before: skincareBefore, after: skincareAfter },
  { title: 'Boho Interior', before: plantBefore, after: plantAfter },
  { title: 'Modern Workspace', before: headphonesBefore, after: headphonesAfter },
  { title: 'Cozy Hygge', before: candleBefore, after: candleAfter },
  { title: 'Luxury Editorial', before: watchBefore, after: watchAfter },
  { title: 'Natural Organic', before: facecreamBefore, after: facecreamAfter },
  { title: 'Urban EDC', before: phonecaseBefore, after: phonecaseAfter },
  { title: 'Beauty Editorial', before: lipstickBefore, after: lipstickAfter },
]

const GalleryCard = ({ title, before, after }) => {
  const [showBefore, setShowBefore] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Detect if device is mobile/touch
  useState(() => {
    const checkMobile = () => {
      setIsMobile('ontouchstart' in window || navigator.maxTouchPoints > 0)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleToggle = () => {
    if (isMobile) {
      setShowBefore(!showBefore)
    }
  }

  return (
    <Box
      component="article"
      onClick={handleToggle}
      onMouseEnter={() => !isMobile && setShowBefore(true)}
      onMouseLeave={() => !isMobile && setShowBefore(false)}
      sx={{
        position: 'relative',
        borderRadius: designSpecs.borderRadius.xl,
        overflow: 'hidden',
        boxShadow: designSpecs.shadows.md,
        cursor: isMobile ? 'pointer' : 'default',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: designSpecs.shadows.xl,
        },
      }}
    >
      {/* After Image (Default) */}
      <Box
        component="img"
        src={after}
        alt={`${title} - AI transformed product photo`}
        draggable={false}
        sx={{
          width: '100%',
          height: '100%',
          aspectRatio: '1/1',
          objectFit: 'cover',
          display: 'block',
          opacity: showBefore ? 0 : 1,
          transition: 'opacity 0.5s ease',
        }}
      />

      {/* Before Image (On Hover/Tap) */}
      <Box
        component="img"
        src={before}
        alt={`${title} - Original product photo`}
        draggable={false}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: showBefore ? 1 : 0,
          transition: 'opacity 0.5s ease',
          pointerEvents: 'none',
        }}
      />

      {/* Hover/Tap Badge */}
      <Chip
        icon={isMobile ? <TouchApp sx={{ fontSize: '1rem !important' }} /> : <Mouse sx={{ fontSize: '1rem !important' }} />}
        label={isMobile ? 'Tap to see original' : 'Hover to see original'}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(8px)',
          color: 'white',
          fontWeight: 500,
          fontSize: designSpecs.typography.sizes.xs,
          height: 28,
          opacity: showBefore ? 0 : 0.9,
          transition: 'opacity 0.3s ease',
          '& .MuiChip-icon': {
            color: 'white',
          },
        }}
      />

      {/* Title */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 70%, transparent 100%)',
          backdropFilter: 'blur(4px)',
        }}
      >
        <Typography
          sx={{
            color: 'white',
            fontWeight: 600,
            fontSize: designSpecs.typography.sizes.base,
            textAlign: 'center',
          }}
        >
          {title}
        </Typography>
      </Box>
    </Box>
  )
}

const IdeasGallery = () => {
  return (
    <Box component="section" sx={{ py: 6, bgcolor: 'white', position: 'relative' }}>
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ mb: 3, color: 'grey.900' }}>
            See What's Possible
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: 'grey.600',
              fontWeight: 400,
              maxWidth: 800,
              mx: 'auto',
            }}
          >
            Explore real transformations across different product categories
          </Typography>
        </Box>

        {/* Gallery Grid - 3x3 */}
        <Grid container spacing={3}>
          {galleryItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <GalleryCard {...item} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

export default IdeasGallery
